import React, { useState, useEffect, useRef } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import PropTypes from "prop-types";

function CircularCountdownTimer({
  duration,
  totalDuration = duration,
  onTimeUp = null,
  onWarning = null,
  warningThreshold = 10,
}) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const intervalRef = useRef(null);
  const deadlineRef = useRef(null);
  const hasWarnedRef = useRef(false);
  const hasTimedUpRef = useRef(false);

  // Keep the latest callbacks in refs so the timer effect below doesn't
  // need them as dependencies. onTimeUp/onWarning are usually passed as
  // inline arrow functions that get a new identity on every parent
  // re-render — depending on them directly would re-run the effect (and
  // re-fire onTimeUp) on every unrelated re-render once timeLeft hits 0.
  const onTimeUpRef = useRef(onTimeUp);
  const onWarningRef = useRef(onWarning);
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
    onWarningRef.current = onWarning;
  }, [onTimeUp, onWarning]);

  useEffect(() => {
    const safeDuration = Math.max(0, Number(duration) || 0);
    deadlineRef.current = Date.now() + safeDuration * 1000;
    setTimeLeft(safeDuration);
    hasWarnedRef.current = false;
    hasTimedUpRef.current = false;

    const updateTimer = () => {
      const remaining = Math.max(
        0,
        Math.ceil((deadlineRef.current - Date.now()) / 1000),
      );
      setTimeLeft(remaining);

      if (remaining <= warningThreshold && !hasWarnedRef.current) {
        hasWarnedRef.current = true;
        if (onWarningRef.current) onWarningRef.current();
      }

      if (remaining === 0 && !hasTimedUpRef.current) {
        hasTimedUpRef.current = true;
        clearInterval(intervalRef.current);
        if (onTimeUpRef.current) onTimeUpRef.current();
      }
    };

    updateTimer();
    intervalRef.current = setInterval(updateTimer, 1000);

    return () => clearInterval(intervalRef.current);
  }, [duration, warningThreshold]);

  const safeTotalDuration = Math.max(1, Number(totalDuration) || 1);
  const percentage = (timeLeft / safeTotalDuration) * 100;

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    const minuteText = hours > 0 ? String(minutes).padStart(2, "0") : minutes;
    const secondText = String(remainingSeconds).padStart(2, "0");
    return hours > 0
      ? `${hours}:${minuteText}:${secondText}`
      : `${minuteText}:${secondText}`;
  };

  return (
    <div style={{ width: 60, height: 60 }}>
      <CircularProgressbar
        value={percentage}
        text={formatTime(timeLeft)}
        styles={buildStyles({
          textColor: "#000",
          textSize: "20px",
          pathColor: timeLeft > warningThreshold ? "#A63D00" : "#B91C1C",
          trailColor: "#FFCAA6",
        })}
      />
    </div>
  );
}

CircularCountdownTimer.propTypes = {
  duration: PropTypes.number.isRequired,
  totalDuration: PropTypes.number,
  onTimeUp: PropTypes.func,
  onWarning: PropTypes.func,
  warningThreshold: PropTypes.number,
};

export default CircularCountdownTimer;
